import { ArrowRight, type LucideIcon } from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { type ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";
import { createStaggerContainer, createStaggerItem, REVEAL_AMOUNT } from "@/src/common/components/animated-section";
import { CtaAnchor } from "@/src/common/components/cta-link";
import { EyebrowBadge } from "@/src/common/components/eyebrow-badge";
import { APP_LINKS } from "@/src/common/constants/app-links";
import { toEntranceVariant, useEntrance } from "@/src/common/hooks/use-entrance";
import { getStringArray } from "@/src/common/utils/i18n.utils";

interface FeatureBlockProps {
	id: string;
	icon: LucideIcon;
	tone?: "ink" | "premium";
	side: "left" | "right";
	visual: ReactNode;
	parallax: boolean;
}

const PARALLAX_PX = 16;

export function FeatureBlock({ id, icon: Icon, tone = "ink", side, visual, parallax }: FeatureBlockProps) {
	const { t } = useTranslation("features");
	const isPremium = tone === "premium";
	const bullets = getStringArray(t(`items.${id}.bullets`, { returnObjects: true }));
	const reduced = useReducedMotion() === true;
	const visualRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLUListElement>(null);
	const entrance = useEntrance(listRef, { amount: REVEAL_AMOUNT, disabled: reduced });
	const { scrollYProgress } = useScroll({ target: visualRef, offset: ["start end", "end start"] });
	const parallaxY = useTransform(scrollYProgress, [0, 1], [PARALLAX_PX, -PARALLAX_PX]);
	const container = createStaggerContainer(reduced, 0.07);
	const item = createStaggerItem(reduced, 12);

	const isVisualRight = side === "right";

	return (
		<div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
			<div className={`flex flex-col gap-6 ${isVisualRight ? "lg:order-1" : "lg:order-2"}`}>
				<div className="flex items-center gap-3">
					<span className="inline-flex size-10 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
						<Icon className="h-5 w-5" />
					</span>
					{isPremium ? (
						<EyebrowBadge tone="accent">{t("premium_badge")}</EyebrowBadge>
					) : (
						<EyebrowBadge tone="ink">{t(`items.${id}.eyebrow`)}</EyebrowBadge>
					)}
				</div>
				<h2 className="text-balance text-3xl font-bold tracking-tight text-foreground md:text-4xl">{t(`items.${id}.title`)}</h2>
				<motion.ul ref={listRef} className="space-y-3" initial={false} animate={toEntranceVariant(entrance)} variants={container}>
					{bullets.map((bullet) => (
						<motion.li key={bullet} variants={item} className="flex items-start gap-3 text-base text-default-600">
							<span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-brand-500" />
							<span>{bullet}</span>
						</motion.li>
					))}
				</motion.ul>
				<div>
					<CtaAnchor href={APP_LINKS.SIGN_UP} variant="outline" size="md" endContent={<ArrowRight className="h-4 w-4" />}>
						{t("try_it_free")}
					</CtaAnchor>
				</div>
			</div>

			<div ref={visualRef} className={`relative ${isVisualRight ? "lg:order-2" : "lg:order-1"}`}>
				<motion.div style={parallax && !reduced ? { y: parallaxY } : undefined}>{visual}</motion.div>
			</div>
		</div>
	);
}
